import { Bluetooth, Camera, CameraOff, Ear, Mic, MicOff, MonitorUp, Settings2, Volume2, VolumeX } from "lucide-react";
import React from "react";
import { type AudioRoute, ROUTE_LABEL, nextRoute } from "../../lib/audioRoute";

type Device = { deviceId: string; label?: string | null };

type Props = {
  voiceJoined: boolean;
  voiceMuted: boolean;
  voiceDeafened: boolean;
  channelName: string;
  participantCount: number;
  voiceAvailable?: boolean;
  devicesOpen: boolean;
  selectedInputId: string | null;
  selectedOutputId: string | null;
  inputs: Device[];
  outputs: Device[];
  needsAudioUnlock: boolean;
  voiceVolumes: Record<string, number>;
  voiceSilenced: Record<string, boolean>;
  remoteStreams: Map<string, MediaStream>;
  toggleMute: () => void;
  toggleDeafen: () => void;
  joinVoice: () => void;
  leaveVoice: () => void;
  toggleDevices: () => void;
  refreshDevices: () => void;
  toggleVideo: () => Promise<void> | void;
  videoEnabled: boolean;
  videoBusy: boolean;
  switchMicrophone: (id: string | null) => Promise<void>;
  applyOutputDevice: (id: string | null) => Promise<void>;
  unlockAudio: () => Promise<void>;
  audioElsRef: React.MutableRefObject<Map<string, HTMLAudioElement>>;
  setNeedsAudioUnlock: (v: boolean) => void;
  setSelectedInputId: (id: string | null) => void;
  setSelectedOutputId: (id: string | null) => void;
  audioRoute?: AudioRoute;
  availableRoutes?: AudioRoute[];
  onChangeAudioRoute?: (route: AudioRoute) => void;
};

export default function VoiceChannelView({
  voiceJoined,
  voiceMuted,
  voiceDeafened,
  channelName,
  participantCount,
  voiceAvailable = true,
  devicesOpen,
  selectedInputId,
  selectedOutputId,
  inputs,
  outputs,
  needsAudioUnlock,
  voiceVolumes,
  voiceSilenced,
  remoteStreams,
  toggleMute,
  toggleDeafen,
  toggleVideo,
  joinVoice,
  leaveVoice,
  toggleDevices,
  refreshDevices,
  videoEnabled,
  videoBusy,
  switchMicrophone,
  applyOutputDevice,
  unlockAudio,
  audioElsRef,
  setNeedsAudioUnlock,
  setSelectedInputId,
  setSelectedOutputId,
  audioRoute = 'earpiece',
  availableRoutes = [],
  onChangeAudioRoute,
}: Props) {
  const outputLabel = outputs.find((device) => device.deviceId === selectedOutputId)?.label || "System output";
  const iconButton = "flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.05] text-fg-2 transition duration-150 hover:bg-white/[0.1] hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:shadow-none";
  const activeButton = "bg-emerald-400/15 text-emerald-200";
  const offButton = "bg-rose-500/15 text-rose-200";

  return (
    <>
      <div className="sr-only">
        {Array.from(remoteStreams.entries()).map(([pid, ms]) => (
          <audio
            key={pid}
            autoPlay
            playsInline
            ref={(el) => {
              if (el) {
                (el as any).srcObject = ms;
                audioElsRef.current.set(pid, el);
                try {
                  if (selectedOutputId && typeof (el as any).setSinkId === "function") {
                    (el as any).setSinkId(selectedOutputId).catch(() => {});
                  }
                } catch { /* noop */ }
                try { el.volume = voiceVolumes[pid] ?? 1; } catch { /* noop */ }
                try { el.muted = voiceDeafened || !!voiceSilenced[pid]; } catch { /* noop */ }
                try {
                  const play = el.play();
                  if (play && typeof (play as any).then === "function") {
                    (play as any).catch(() => setNeedsAudioUnlock(true));
                  }
                } catch { setNeedsAudioUnlock(true); }
              } else {
                audioElsRef.current.delete(pid);
              }
            }}
          />
        ))}
      </div>

      <div className="relative z-20 flex justify-center px-3 pb-4 pt-1 sm:pb-5">
        <div className="relative w-full max-w-2xl">
          {devicesOpen && (
            <div className="absolute bottom-[calc(100%+12px)] right-0 z-30 w-full max-w-sm rounded-2xl bg-neutral-950/95 p-4 shadow-[0_22px_65px_rgba(0,0,0,0.55)] backdrop-blur-xl">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-xs text-emerald-300/70">Devices</div>
                  <div className="mt-1 text-sm text-fg-2">Audio routing</div>
                </div>
                <button className="rounded-full px-3 py-1 text-xs text-fg-3 transition hover:bg-white/5 hover:text-white" onClick={refreshDevices} type="button">
                  Refresh
                </button>
              </div>
              <div className="space-y-3">
                <label className="block space-y-1.5">
                  <span className="text-meta text-fg-3">Microphone</span>
                  <select
                    className="w-full rounded-xl bg-neutral-900 px-3 py-2 text-sm text-white outline-none transition focus:outline-none focus:ring-1 focus:ring-emerald-400/45" style={{ colorScheme: 'dark' }}
                    value={selectedInputId ?? ""}
                    onChange={async (event) => {
                      const id = event.target.value || null;
                      setSelectedInputId(id);
                      await switchMicrophone(id);
                    }}
                  >
                    <option value="">System default</option>
                    {inputs.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || device.deviceId}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block space-y-1.5">
                  <span className="text-meta text-fg-3">Output</span>
                  <select
                    className="w-full rounded-xl bg-neutral-900 px-3 py-2 text-sm text-white outline-none transition focus:outline-none focus:ring-1 focus:ring-emerald-400/45" style={{ colorScheme: 'dark' }}
                    value={selectedOutputId ?? ""}
                    onChange={async (event) => {
                      const id = event.target.value || null;
                      setSelectedOutputId(id);
                      await applyOutputDevice(id);
                    }}
                  >
                    <option value="">System default</option>
                    {outputs.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || device.deviceId}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}

          {needsAudioUnlock && (
            <div className="mb-3 flex items-center justify-between gap-3 rounded-2xl bg-amber-400/10 px-4 py-2.5 text-sm text-amber-100 ring-1 ring-amber-400/25 backdrop-blur-xl">
              <span>Audio playback is paused.</span>
              <button className="rounded-full bg-amber-300/15 px-3 py-1 text-xs font-medium transition hover:bg-amber-300/25" onClick={() => { void unlockAudio(); }}>
                Unlock audio
              </button>
            </div>
          )}

          <div className={`rounded-[26px] bg-neutral-950/75 p-2.5 shadow-[0_22px_70px_rgba(0,0,0,0.5)] backdrop-blur-xl transition ${voiceJoined ? "ring-1 ring-emerald-400/20" : ""}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 px-3 pb-2 pt-1">
              <div className="flex min-w-0 items-center gap-2 text-xs">
                <span className={`h-2 w-2 rounded-full ${voiceJoined ? "bg-emerald-400" : "bg-white/25"}`} />
                <span className={voiceJoined ? "text-emerald-200" : "text-fg-2"}>
                  {voiceJoined ? `Connected to #${channelName}` : `#${channelName}`}
                </span>
                <span className="text-fg-3">/</span>
                <span className="text-fg-3">{participantCount} online</span>
              </div>
              {voiceJoined && (
                <div className="hidden items-center gap-3 text-meta text-fg-3 sm:flex">
                  <span>{voiceMuted ? "Mic muted" : "Mic active"}</span>
                  <span className="max-w-36 truncate">{voiceDeafened ? "Audio off" : `Output: ${outputLabel}`}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 rounded-2xl bg-black/20 px-2 py-2 sm:justify-between">
              <button
                disabled={!voiceAvailable && !voiceJoined}
                title={voiceJoined ? "Leave call" : (!voiceAvailable ? "Voice is not configured on this server" : undefined)}
                aria-label={voiceJoined ? "Leave call" : "Join voice"}
                className={`flex h-11 items-center justify-center rounded-full text-sm font-medium transition duration-150 active:scale-95 ${
                  voiceJoined
                    ? "w-11 bg-rose-500/20 text-rose-200 hover:bg-rose-500/30"
                    : !voiceAvailable
                    ? "px-5 bg-white/5 text-fg-3 cursor-not-allowed"
                    : "px-5 bg-emerald-400 text-neutral-950 hover:bg-emerald-300"
                }`}
                onClick={voiceJoined ? leaveVoice : joinVoice}
              >
                {voiceJoined ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 rotate-[135deg]"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 5.15 12.8 19.79 19.79 0 0 1 2.08 4.16 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.89.33 1.77.62 2.61a2 2 0 0 1-.45 2.11L8 9.66a16 16 0 0 0 6.34 6.34l1.22-1.23a2 2 0 0 1 2.11-.45c.84.29 1.72.5 2.61.62A2 2 0 0 1 22 16.92z"/></svg>
                ) : (!voiceAvailable ? "Voice unavailable" : "Join Voice")}
              </button>
              <div className="flex items-center gap-2">
                <button className={`${iconButton} ${voiceMuted ? offButton : voiceJoined ? activeButton : ""}`} onClick={toggleMute} disabled={!voiceJoined} title={voiceMuted ? "Unmute microphone" : "Mute microphone"} aria-label={voiceMuted ? "Unmute microphone" : "Mute microphone"}>
                  {voiceMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </button>
                <button className={`${iconButton} ${voiceDeafened ? offButton : voiceJoined ? activeButton : ""}`} onClick={toggleDeafen} disabled={!voiceJoined} title={voiceDeafened ? "Restore audio" : "Deafen audio"} aria-label={voiceDeafened ? "Restore audio" : "Deafen audio"}>
                  {voiceDeafened ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                </button>
                {availableRoutes.length > 1 && onChangeAudioRoute && voiceJoined && (
                  <button
                    className={`${iconButton} ${audioRoute === 'bluetooth' ? activeButton : ''}`}
                    onClick={() => onChangeAudioRoute(nextRoute(audioRoute, availableRoutes))}
                    title={`Audio: ${ROUTE_LABEL[audioRoute]} — tap to switch`}
                    aria-label={`Audio output: ${ROUTE_LABEL[audioRoute]}`}
                  >
                    {audioRoute === 'bluetooth' ? (
                      <Bluetooth className="h-5 w-5" />
                    ) : audioRoute === 'speaker' ? (
                      <Volume2 className="h-5 w-5" />
                    ) : (
                      <Ear className="h-5 w-5" />
                    )}
                  </button>
                )}
                <button className={`${iconButton} ${videoEnabled ? activeButton : ""}`} onClick={() => toggleVideo()} disabled={!voiceJoined || videoBusy} title={videoEnabled ? "Stop camera" : "Start camera"} aria-label={videoEnabled ? "Stop camera" : "Start camera"}>
                  {videoEnabled ? <Camera className="h-5 w-5" /> : <CameraOff className="h-5 w-5" />}
                </button>
                <button className={iconButton} disabled title="Screen share not available yet" aria-label="Screen share not available yet">
                  <MonitorUp className="h-5 w-5" />
                </button>
                <button className={`${iconButton} ${devicesOpen ? activeButton : ""}`} onClick={toggleDevices} title="Audio device settings" aria-label="Audio device settings">
                  <Settings2 className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
